import { FlowGraph } from '../FlowGraph';
import { UndoRedoEntry } from '../undoRedo';
import type { PainterOptions, NodeId, PainterMode } from 'web';
import type { PainterData } from '@/types/node';
import type { WebColor } from '@/UI/ColorPicker/ColorPicker';

const MAX_DIMENSION = 4096;
const MAX_BRUSH_SIZE_FACTOR = 0.3;
const BRUSH_SIZE_STEP = 20;
const MIN_BRUSH_SIZE_STEP = 10;
const MIN_BRUSH_SIZE_FACTOR = 0.01;
const MIN_BRUSH_SIZE_FLOOR = 2;

function calcBrushSizeRange(maxDimension: number): readonly [min: number, max: number] {
  let maxBrushSize = Math.floor(Math.round(maxDimension * MAX_BRUSH_SIZE_FACTOR) / BRUSH_SIZE_STEP) * BRUSH_SIZE_STEP;
  maxBrushSize = Math.max(maxBrushSize, BRUSH_SIZE_STEP);

  let minBrushSize =
    Math.floor(Math.round(maxDimension * MIN_BRUSH_SIZE_FACTOR) / MIN_BRUSH_SIZE_STEP) * MIN_BRUSH_SIZE_STEP;
  minBrushSize = Math.max(minBrushSize, MIN_BRUSH_SIZE_FLOOR);
  minBrushSize = Math.min(minBrushSize, maxBrushSize);

  return [minBrushSize, maxBrushSize];
}

// FIXME: default options on TS
const DEFAULT_PAINTER_OPTIONS: PainterOptions = {
  width: 1024,
  height: 1024,
  lockAspectRatio: true,
  backgroundColor: {
    r: 0,
    g: 0,
    b: 0,
    a: 1.0,
  },
  brushColor: {
    r: 255,
    g: 255,
    b: 255,
    a: 1.0,
  },
  brushSize: 30.0,
  brushHardness: 1.0,
  eraserSize: 30.0,
  eraserHardness: 1.0,
  eraserOpacity: 1.0,
};

export class PainterView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getOptions(): PainterOptions {
    return this.graph.store.getNodeData<PainterData>(this.nodeId).options ?? DEFAULT_PAINTER_OPTIONS;
  }

  hasInputImage(): boolean {
    return this.graph.wasm.call((wasm) => wasm.nodeInputType(this.nodeId, 'image')) === 'image';
  }

  private setPainterOptions(options: Partial<PainterOptions>, ongoing: boolean) {
    const undoEntry = this.graph.wasmChange((wasm) => {
      const prevOptions = this.getOptions();
      return wasm.setPainterOptions(this.nodeId, { ...prevOptions, ...options });
    });

    this.graph.updateNodeOutputs(this.nodeId, ongoing);
    return undoEntry;
  }

  setBackgroundColor(color: WebColor, ongoing: boolean) {
    this.graph.edit(() => this.setPainterOptions({ backgroundColor: color }, ongoing), ongoing);
  }

  setBrushColor(color: WebColor, ongoing: boolean) {
    this.graph.edit(() => this.setPainterOptions({ brushColor: color }, ongoing), ongoing);
  }

  getEffectiveMaxDimension(): number {
    const input = this.graph.evaluateInput(this.nodeId, 'image');
    if (input?.type === 'image') {
      return Math.max(input.value.width, input.value.height);
    }
    const { width, height } = this.getOptions();
    return Math.max(width, height);
  }

  getBrushSizeRange(): readonly [min: number, max: number] {
    return calcBrushSizeRange(this.getEffectiveMaxDimension());
  }

  private clampBrushSizeUpdates(maxDimension: number): Partial<PainterOptions> | undefined {
    const [min, max] = calcBrushSizeRange(maxDimension);
    const { brushSize, eraserSize } = this.getOptions();
    const updates: Partial<PainterOptions> = {};
    if (brushSize < min || brushSize > max) {
      updates.brushSize = Math.min(max, Math.max(min, brushSize));
    }
    if (eraserSize < min || eraserSize > max) {
      updates.eraserSize = Math.min(max, Math.max(min, eraserSize));
    }
    return Object.keys(updates).length > 0 ? updates : undefined;
  }

  clampBrushSize() {
    const updates = this.clampBrushSizeUpdates(this.getEffectiveMaxDimension());
    if (updates) {
      this.setPainterOptions(updates, false);
    }
  }

  private clampBrushSizeEntry(newCanvasWidth: number, newCanvasHeight: number): UndoRedoEntry | undefined {
    const imageInput = this.graph.evaluateInput(this.nodeId, 'image');
    const maxDim =
      imageInput?.type === 'image'
        ? Math.max(imageInput.value.width, imageInput.value.height)
        : Math.max(newCanvasWidth, newCanvasHeight);
    const updates = this.clampBrushSizeUpdates(maxDim);
    if (updates) {
      return this.setPainterOptions(updates, false);
    }
    return undefined;
  }

  setBrushSize(size: number, ongoing: boolean = false) {
    this.graph.edit(() => this.setPainterOptions({ brushSize: size }, ongoing), ongoing);
  }

  setBrushHardness(hardness: number, ongoing: boolean = false) {
    const clamped = Math.min(1, Math.max(0, hardness));
    this.graph.edit(() => this.setPainterOptions({ brushHardness: clamped }, ongoing), ongoing);
  }

  setEraserSize(size: number, ongoing: boolean = false) {
    this.graph.edit(() => this.setPainterOptions({ eraserSize: size }, ongoing), ongoing);
  }

  setEraserHardness(hardness: number, ongoing: boolean = false) {
    const clamped = Math.min(1, Math.max(0, hardness));
    this.graph.edit(() => this.setPainterOptions({ eraserHardness: clamped }, ongoing), ongoing);
  }

  setEraserOpacity(opacity: number, ongoing: boolean = false) {
    const clamped = Math.min(1, Math.max(0, opacity));
    this.graph.edit(() => this.setPainterOptions({ eraserOpacity: clamped }, ongoing), ongoing);
  }

  setMode(mode: PainterMode) {
    this.graph.wasm.call((wasm) => wasm.setPainterMode(this.nodeId, mode));
  }

  reset() {
    this.graph.edit(() => this.resetMarks(), false);
  }

  private resetMarks() {
    const undoEntry = this.graph.wasmChange((wasm) => wasm.resetPainterMarks(this.nodeId));
    this.graph.updateNodeOutputs(this.nodeId, false);
    return undoEntry;
  }

  // DIMENSIONS
  setWidth(value: number, ongoing: boolean = false) {
    if (value <= 0) return;

    const options = this.getOptions();
    const { lockAspectRatio, width, height } = options;
    const aspectRatio = width / height;

    const clampedValue = Math.min(value, MAX_DIMENSION);
    let [newWidth, newHeight] = lockAspectRatio ? [clampedValue, clampedValue / aspectRatio] : [clampedValue, height];
    if (newHeight > MAX_DIMENSION) {
      newHeight = MAX_DIMENSION;
      newWidth = newHeight * aspectRatio;
    }

    const rw = Math.round(newWidth);
    const rh = Math.round(newHeight);
    this.graph.edit(() => {
      const entry = this.setPainterOptions({ width: rw, height: rh }, ongoing);
      if (!ongoing) {
        const clampEntry = this.clampBrushSizeEntry(rw, rh);
        if (clampEntry) entry.add(clampEntry);
      }
      return entry;
    }, ongoing);
  }

  setHeight(value: number, ongoing: boolean = false) {
    if (value <= 0) return;
    const options = this.getOptions();
    const { lockAspectRatio, width, height } = options;
    const aspectRatio = width / height;

    const clampedValue = Math.min(value, MAX_DIMENSION);
    let [newWidth, newHeight] = lockAspectRatio ? [clampedValue * aspectRatio, clampedValue] : [width, clampedValue];
    if (newWidth > MAX_DIMENSION) {
      newWidth = MAX_DIMENSION;
      newHeight = newWidth / aspectRatio;
    }

    const rw = Math.round(newWidth);
    const rh = Math.round(newHeight);
    this.graph.edit(() => {
      const entry = this.setPainterOptions({ width: rw, height: rh }, ongoing);
      if (!ongoing) {
        const clampEntry = this.clampBrushSizeEntry(rw, rh);
        if (clampEntry) entry.add(clampEntry);
      }
      return entry;
    }, ongoing);
  }

  toggleLockAspectRatio() {
    this.graph.edit(
      () => this.setPainterOptions({ lockAspectRatio: !this.getOptions().lockAspectRatio }, false),
      false,
    );
  }
}
